/*
 * aura-header.js: shell behavior (spec 04).
 * <aura-header>     sticky bar that hides while scrolling down and returns on scroll up or focus
 * <aura-mega-menu>  disclosure: click, or 150ms hover; Esc / outside click / 300ms after leave close it
 * <aura-offer-band> closes for the session, hides after its end date, opens its terms sheet
 * Also: mobile menu dialog (reopens where it was left) and its rolling disclosures, nav analytics, Cart chip opens the drawer (not on phones).
 * The menus' collection lists lead with this shopper's collections (aura-taste.js).
 */
(() => {
  const aura = () => window.aura || { track() {}, openDialog(d) { d.showModal(); } };

  /* Up to LEAD_MAX collections that clearly interest this shopper (a product of theirs looked at this session,
     or a few visits) move to the top of each list, most first; the rest keep the admin menu's order. The
     mobile lists keep See all first, and the color family swatches keep spectrum order */
  const LEAD_MAX = 4;
  const LEAD_MIN = 1;
  function leadWithTaste(root) {
    const taste = window.aura && window.aura.taste;
    const liked = taste ? taste.collections() : new Map();
    if (!liked.size) return;
    root.querySelectorAll('.mega__links, .mobile-menu__children').forEach((ul) => {
      const head = ul.matches('.mobile-menu__children') ? [...ul.children].slice(0, 1) : [];
      const items = [...ul.children].slice(head.length).map((li) => {
        const a = li.querySelector('a[href]');
        return { li, a, w: (a && liked.get(taste.handleOf(a.href))) || 0 };
      });
      const lead = items.filter((x) => x.w >= LEAD_MIN).sort((x, y) => y.w - x.w).slice(0, LEAD_MAX);
      if (!lead.length) return;
      lead.forEach((x) => { x.a.dataset.forYou = ''; });
      ul.replaceChildren(...head, ...lead.map((x) => x.li), ...items.filter((x) => !lead.includes(x)).map((x) => x.li));
    });
  }

  class AuraHeader extends HTMLElement {
    connectedCallback() {
      leadWithTaste(this);
      this.lastY = window.scrollY;
      if (this.dataset.hideOnScroll === 'true') {
        let ticking = false;
        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => { this.onScroll(); ticking = false; });
        }, { passive: true });
      }
      this.addEventListener('focusin', () => this.classList.remove('is-hidden'));
      /* Desktop: when the nav links are wider than the bar, a vertical wheel scrolls them sideways.
         At either end, and over an open panel, the wheel scrolls as usual. */
      const nav = this.querySelector('.site-nav');
      nav?.addEventListener('wheel', (e) => {
        if (e.target.closest('.mega') || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        const max = nav.scrollWidth - nav.clientWidth;
        const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        if (max <= 0 || (dy < 0 && nav.scrollLeft <= 0) || (dy > 0 && nav.scrollLeft >= max - 1)) return;
        e.preventDefault();
        nav.scrollLeft += dy;
      }, { passive: false });
      const menuBtn = this.querySelector('[data-menu-open]');
      const menu = menuBtn && document.getElementById(menuBtn.getAttribute('aria-controls'));
      if (menu) {
        const restoreMenu = this.keepMenuPlace(menu);
        menuBtn.addEventListener('click', () => {
          aura().openDialog(menu, menuBtn);
          restoreMenu();
          aura().track('aura_mobile_menu_opened');
        });
      }
      this.addEventListener('click', (e) => {
        const link = e.target.closest('a[data-nav-item]');
        if (link) aura().track('aura_nav_clicked', { item: link.dataset.navItem, level: link.dataset.navLevel || 'top', destination: link.getAttribute('href'), for_you: 'forYou' in link.dataset });
        const card = e.target.closest('.mega .card');
        if (card) aura().track('aura_nav_clicked', { item: card.dataset.handle, level: card.closest('.mega__products') ? 'mega_best_sellers' : 'mega_popular', destination: card.querySelector('.card__link')?.getAttribute('href') });
      });
      document.addEventListener('click', (e) => {
        const link = e.target.closest('.site-footer a[data-nav-item]');
        if (link) aura().track('aura_nav_clicked', { item: link.dataset.navItem, level: 'footer', destination: link.getAttribute('href') });
      });
    }
    /* The mobile menu reopens where it was left, on this page or a later one within MENU_PLACE_TTL: the same
       disclosures open, scrolled to the same place. Phone tabs keep sessionStorage for days, so an older place
       is dropped and the menu starts fresh. Scroll is noted as it happens, since a closed dialog reads 0. */
    keepMenuPlace(menu) {
      const key = 'aura.menu.place';
      const MENU_PLACE_TTL = 30 * 60 * 1000;
      const scrollers = [menu, menu.querySelector('.sheet__body')];
      const disclosures = [...menu.querySelectorAll('.disclosure')];
      let place = null;
      try { place = JSON.parse(sessionStorage.getItem(key)); } catch (e) { /* private mode */ }
      if (!(Date.now() - place?.at < MENU_PLACE_TTL)) {
        place = null;
        try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
      }
      const top = Array.isArray(place?.top) ? place.top : [0, 0];
      (place?.open || []).forEach((i) => { if (disclosures[i]) disclosures[i].open = true; });
      scrollers.forEach((el, i) => el?.addEventListener('scroll', () => { if (menu.open) top[i] = el.scrollTop; }, { passive: true }));
      const save = () => {
        const open = disclosures.flatMap((d, i) => (d.open && d.dataset.closing == null ? [i] : []));
        try { sessionStorage.setItem(key, JSON.stringify({ open, top, at: Date.now() })); } catch (e) { /* ignore */ }
      };
      menu.addEventListener('close', save);
      window.addEventListener('pagehide', () => { if (menu.open) save(); });
      return () => scrollers.forEach((el, i) => { if (el) el.scrollTop = top[i] || 0; });
    }
    onScroll() {
      const y = window.scrollY;
      const open = this.querySelector('[aria-expanded="true"]') || this.contains(document.activeElement);
      if (y > 400 && y > this.lastY && !open) this.classList.add('is-hidden');
      else if (y < this.lastY) this.classList.remove('is-hidden');
      this.lastY = y;
    }
  }

  class AuraMegaMenu extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button[aria-expanded]');
      this.panel = document.getElementById(this.button.getAttribute('aria-controls'));
      this.button.addEventListener('click', () => this.toggle(this.button.getAttribute('aria-expanded') !== 'true', 'click'));
      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => { this.loadPopular(); clearTimeout(this.closeT); this.openT = setTimeout(() => this.toggle(true, 'hover'), 150); });
        this.addEventListener('mouseleave', () => { clearTimeout(this.openT); this.closeT = setTimeout(() => this.toggle(false), 300); });
      }
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) { this.toggle(false); this.button.focus(); }
        if (['ArrowDown', 'ArrowUp'].includes(e.key) && this.isOpen()) {
          const links = [...this.panel.querySelectorAll('a')];
          const i = links.indexOf(document.activeElement);
          const next = e.key === 'ArrowDown' ? links[i + 1] || links[0] : links[i - 1] || links[links.length - 1];
          if (next) { e.preventDefault(); next.focus(); }
        }
      });
      document.addEventListener('click', (e) => { if (this.isOpen() && !this.contains(e.target)) this.toggle(false); });
      this.addEventListener('focusout', (e) => { if (this.isOpen() && !this.contains(e.relatedTarget)) this.toggle(false); });
      const refit = () => { if (this.isOpen()) this.fit(); };
      window.addEventListener('resize', refit, { passive: true });
      window.addEventListener('scroll', refit, { passive: true });
    }
    isOpen() { return this.button.getAttribute('aria-expanded') === 'true'; }
    /* The panel's max-height starts at the header's bottom edge, which moves while the offer band scrolls away */
    fit() {
      const top = this.closest('.site-header')?.getBoundingClientRect().bottom;
      if (top != null) this.panel.style.setProperty('--mega-top', `${Math.max(0, Math.round(top))}px`);
    }
    /* Popular colors and best sellers: cards come from sections/mega-popular.liquid or mega-best-sellers.liquid
       in best-selling order, fetched once, on the first hover or open */
    loadPopular() {
      const rail = this.panel.querySelector('[data-popular-url]');
      if (!rail || rail.dataset.loading) return;
      rail.dataset.loading = '';
      const drop = () => rail.closest('.mega__popular, .mega__products')?.remove();
      fetch(rail.dataset.popularUrl)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          const cards = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-popular-cards]');
          if (!cards || !cards.children.length) return drop();
          rail.replaceChildren(...cards.children);
          rail.removeAttribute('aria-busy');
        })
        .catch(drop);
    }
    toggle(open, trigger) {
      if (open === this.isOpen()) return;
      if (open) {
        /* Another panel already open: swap without the drawer roll (contents still fade in) */
        const prev = [...document.querySelectorAll('aura-mega-menu')].filter((m) => m !== this && m.isOpen && m.isOpen());
        if (prev.length) [this, ...prev].forEach((m) => { m.panel.dataset.swap = ''; });
        prev.forEach((m) => m.toggle(false));
        this.loadPopular();
        this.fit();
      }
      this.button.setAttribute('aria-expanded', String(open));
      this.panel.hidden = !open;
      if (open && this.panel.dataset.swap != null) {
        void this.panel.offsetHeight; /* commit the swap with transitions off */
        document.querySelectorAll('.mega[data-swap]').forEach((p) => delete p.dataset.swap);
      }
      if (open) aura().track('aura_mega_menu_opened', { trigger: trigger || 'keyboard' });
    }
  }

  class AuraOfferBand extends HTMLElement {
    connectedCallback() {
      const id = this.dataset.offerId;
      const ends = this.dataset.ends;
      const today = new Date().toISOString().slice(0, 10);
      let closed = null;
      try { closed = sessionStorage.getItem('aura.offer.closed'); } catch (e) { /* private mode */ }
      if (closed === id || (ends && ends < today)) { this.remove(); return; }
      this.querySelector('[data-offer-close]')?.addEventListener('click', () => {
        try { sessionStorage.setItem('aura.offer.closed', id); } catch (e) { /* ignore */ }
        aura().track('aura_offer_band_closed', { offer_id: id });
        this.remove();
      });
      this.querySelector('[data-offer-terms]')?.addEventListener('click', (e) => {
        aura().openDialog(document.getElementById(e.currentTarget.getAttribute('aria-controls')), e.currentTarget);
        aura().track('aura_offer_band_clicked', { offer_id: id });
        aura().track('aura_offer_terms_opened', { offer_id: id });
      });
      this.querySelector('a.offer-band__text')?.addEventListener('click', () => aura().track('aura_offer_band_clicked', { offer_id: id }));
      this.addEventListener('click', (e) => {
        const copy = e.target.closest('[data-copy]');
        if (!copy) return;
        navigator.clipboard?.writeText(copy.dataset.copy).then(() => aura().toast && aura().toast(copy.dataset.copy));
      });
    }
  }

  if (!customElements.get('aura-header')) customElements.define('aura-header', AuraHeader);
  if (!customElements.get('aura-mega-menu')) customElements.define('aura-mega-menu', AuraMegaMenu);
  if (!customElements.get('aura-offer-band')) customElements.define('aura-offer-band', AuraOfferBand);

  /* Mobile search: the magnifier opens the omnibox as a full-width bar under the header */
  const setSearch = (btn, open) => {
    const box = document.getElementById(btn.getAttribute('aria-controls'));
    if (!box) return;
    btn.setAttribute('aria-expanded', String(open));
    if (open) {
      box.dataset.open = '';
      box.querySelector('input[type="search"]')?.focus();
      aura().track('aura_search_focused', { surface: 'header_mobile' });
    } else {
      delete box.dataset.open;
      box.querySelector('.omnibox__panel')?.setAttribute('hidden', '');
    }
  };
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-omnibox-open]');
    if (openBtn) { setSearch(openBtn, openBtn.getAttribute('aria-expanded') !== 'true'); return; }
    const closeBtn = e.target.closest('[data-omnibox-close]');
    const btn = document.querySelector('[data-omnibox-open][aria-expanded="true"]');
    if (!btn) return;
    if (closeBtn || !e.target.closest('aura-omnibox')) { setSearch(btn, false); if (closeBtn) btn.focus(); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const btn = document.querySelector('[data-omnibox-open][aria-expanded="true"]');
    if (btn) { setSearch(btn, false); btn.focus(); }
  });

  /* Mobile menu disclosures roll open and shut, like the desktop mega panel: the summary's toggle is taken
     over so the height animates both ways, and the body drops in a beat behind. A tap mid-roll reverses it
     from where it is. Timings are the motion tokens, which reduced motion already shortens. */
  const roll = (details) => {
    const opening = !details.open || details.dataset.closing != null;
    const from = details.offsetHeight;
    details.rolling?.cancel();
    details.open = true;
    if (opening) delete details.dataset.closing;
    else details.dataset.closing = '';
    const to = opening ? details.offsetHeight : details.querySelector('summary').offsetHeight;
    const css = getComputedStyle(document.documentElement);
    const token = (name) => css.getPropertyValue(name).trim();
    const slow = parseFloat(token('--dur-slow')) || 0;
    details.style.overflow = 'hidden';
    details.rolling = details.animate({ height: [`${from}px`, `${to}px`] }, opening
      ? { duration: slow, easing: token('--ease-out') }
      : { duration: parseFloat(token('--dur-base')) || 0, easing: token('--ease-standard') });
    if (opening) {
      details.querySelector('.disclosure__body')?.animate(
        { opacity: [0, 1], transform: ['translateY(-8px)', 'none'] },
        { duration: slow, easing: token('--ease-out'), delay: 60, fill: 'backwards' }
      );
    }
    details.rolling.onfinish = () => {
      details.rolling = null;
      details.style.overflow = '';
      if (!opening) { details.open = false; delete details.dataset.closing; }
    };
  };
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('.mobile-menu .disclosure > summary');
    if (!summary) return;
    e.preventDefault();
    roll(summary.parentElement);
  });

  /* The cart button opens the drawer when there is one, except on phones (aura.js skips the drawer below
     768px); it stays a link to /cart otherwise */
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-cart-open]');
    const drawer = document.getElementById('CartDrawer');
    if (!chip || !drawer || window.matchMedia('(max-width: 767px)').matches) return;
    e.preventDefault();
    aura().openDialog(drawer, chip);
    aura().track('aura_cart_opened', { trigger: 'icon' });
  });
})();
